import React from "react";
import { Box, Typography, Container, Grid, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { TrendingUp, Headphones, Activity, DollarSign } from "lucide-react";

const SectionContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  backgroundColor: "#ffffff",
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 700,
  textAlign: "center",
  marginBottom: theme.spacing(3),
  letterSpacing: "-0.01em",
  [theme.breakpoints.down("md")]: {
    fontSize: "2rem",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.75rem",
  },
}));

const TitleForeground = styled("span")({
  color: "#1D47A4",
});

const TitlePrimary = styled("span")({
  color: "#3567D6",
});

const Description = styled(Typography)(({ theme }) => ({
  color: "#4C4C4C",
  lineHeight: 1.75,
  fontSize: "1.125rem",
  textAlign: "center",
  maxWidth: "800px",
  margin: "0 auto",
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down("md")]: {
    fontSize: "1rem",
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9375rem",
    marginBottom: theme.spacing(5),
  },
}));

const CardContainer = styled(motion.div)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  padding: theme.spacing(4),
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(53, 103, 214, 0.15)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const IconContainer = styled(Box)(({ theme, bgcolor }) => ({
  width: "56px",
  height: "56px",
  borderRadius: "12px",
  backgroundColor: bgcolor || "rgba(53, 103, 214, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    width: "48px",
    height: "48px",
    borderRadius: "10px",
  },
}));

const CardHeading = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#1D47A4",
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.9375rem",
  color: "#4C4C4C",
  lineHeight: 1.7,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
  },
}));

const CTAContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(8),
  [theme.breakpoints.down("md")]: {
    marginTop: theme.spacing(6),
  },
}));

const HeroButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#3567D6",
  color: "#FFFFFF",
  padding: theme.spacing(1.5, 4),
  fontSize: "1.125rem",
  fontWeight: 600,
  borderRadius: "12px",
  textTransform: "none",
  boxShadow: "0 4px 16px rgba(53, 103, 214, 0.3)",
  "&:hover": {
    backgroundColor: "#1D47A4",
    boxShadow: "0 6px 24px rgba(53, 103, 214, 0.4)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.25, 3),
    fontSize: "1rem",
  },
}));

const metrics = [
  {
    icon: TrendingUp,
    label: "Patient Retention",
    description: "Increase in patient retention with AI-driven engagement and personalized care",
    iconColor: "#3567D6",
    iconBgColor: "rgba(53, 103, 214, 0.1)",
  },
  {
    icon: Headphones,
    label: "Support Queries",
    description: "Reduction in support queries through smart video reports and clear explanations",
    iconColor: "#4674D9",
    iconBgColor: "rgba(70, 116, 217, 0.1)",
  },
  {
    icon: Activity,
    label: "Proactive Care",
    description: "More patients engaged in proactive monitoring and preventive healthcare",
    iconColor: "#1D47A4",
    iconBgColor: "rgba(29, 71, 164, 0.1)",
  },
  {
    icon: DollarSign,
    label: "Revenue Growth",
    description: "Revenue increase through digital convenience tools and better patient experience",
    iconColor: "#022674",
    iconBgColor: "rgba(2, 38, 116, 0.1)",
  },
];

const EngagementSection = () => {
  return (
    <SectionContainer id="about">
      <Container>
        {/* Section Title */}
        <SectionTitle>
          <TitleForeground>What Our </TitleForeground>
          <TitlePrimary>Engagement Tools Deliver</TitlePrimary>
        </SectionTitle>

        <Description>
          At NirogGyan, we build powerful engagement solutions that bridge the communication gap
          between labs, doctors, and patients. Our AI-based tools simplify medical data, deliver
          clarity instantly, and help you turn every report into a meaningful care journey.
        </Description>

        {/* Cards Grid */}
        <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={metric.label}>
              <CardContainer
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <IconContainer bgcolor={metric.iconBgColor}>
                  <metric.icon
                    style={{
                      width: "28px",
                      height: "28px",
                      color: metric.iconColor,
                    }}
                  />
                </IconContainer>

                <CardHeading>{metric.label}</CardHeading>
                <CardDescription>{metric.description}</CardDescription>
              </CardContainer>
            </Grid>
          ))}
        </Grid>

        {/* CTA Button */}
        <CTAContainer>
          <HeroButton 
            size="large"
            onClick={() => {
              window.open("https://www.niro.health/", "_blank", "noopener,noreferrer");
            }}
          >
            Discover AI Tools
          </HeroButton>
        </CTAContainer>
      </Container>
    </SectionContainer>
  );
};

export default EngagementSection;
